/**
 * A teacher's regular hours (D-674).
 *
 * <b>Positive, and that is the whole design.</b> She records when she IS at the
 * studio, not when she is not — because most teachers here hold another job, and
 * a negative model would ask her to black out five days plus every weekday
 * daytime. A gap she forgets in a negative model becomes a bookable slot she
 * cannot do; a gap she forgets here just means fewer bookings.
 */

/** A weekday, in the order a week is read rather than the order an enum happens to be in. */
export const WEEKDAYS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const

export type Weekday = (typeof WEEKDAYS)[number]

/**
 * One stretch she is at the studio.
 *
 * <b>Null times mean she marked the day as one she is not in</b>, which is a
 * different answer from having no row for that day at all. No row is "she has
 * not said" and behaves exactly as the app did before this feature; this is
 * "she has said no" and refuses a parent.
 */
export type HoursRow = {
  day: Weekday
  startTime: string | null
  endTime: string | null
  /** A room she asks to hold for this stretch, Friday to Sunday only (D-710). */
  holdRoomId?: string | null
  /** Read back only: the studio has not answered yet, or the room is held (D-711). */
  holdState?: 'waiting' | 'held' | null
}

/** One date a new hold's room is taken on, and the rooms free instead (D-711). */
export type HoldClash = {
  day: Weekday
  startTime: string
  endTime: string
  date: string
  freeRooms: { id: string; name: string }[]
}

/** What she chose for a taken date: another room, or null to skip it. */
export type HoldChoice = {
  day: Weekday
  startTime: string
  endTime: string
  date: string
  roomId: string | null
}

/**
 * The save was refused because some hold dates are taken; nothing was written.
 *
 * <b>Its own error, not a sentence</b>, because the panel answers it with a
 * choice per date rather than by showing words (`asked`: "maybe allow for that
 * weekend date they could just book a diff room").
 */
export class HoldClashError extends Error {
  readonly clashes: HoldClash[]

  constructor(clashes: HoldClash[]) {
    super('Some of those dates are taken.')
    this.clashes = clashes
  }
}

/** Friday to Sunday only; Monday to Thursday is the studio's own schedule (D-712). */
export function mayHold(day: Weekday): boolean {
  return day === 'Friday' || day === 'Saturday' || day === 'Sunday'
}

/**
 * Where her week stands.
 *
 * <b>Two states since D-709</b>: nothing recorded, or live. Nothing waits on the
 * studio any more, so there is no `pending` or `declined`.
 */
export type HoursStatus = 'none' | 'live'

export type MyHours = {
  status: HoursStatus
  /** The season's last day — these hours stop there (D-674). */
  endsOn: string | null
  days: HoursRow[]
}

export async function fetchMyHours(): Promise<MyHours> {
  const res = await fetch('/api/teacher/hours')

  // A parent has no staff row and so no hours; the panel simply does not render.
  if (res.status === 404) return { status: 'none', endsOn: null, days: [] }
  if (!res.ok) throw new Error(`Could not load your hours (${res.status})`)

  return res.json()
}

export async function saveMyHours(days: HoursRow[], holdChoices: HoldChoice[] = []): Promise<MyHours> {
  const res = await fetch('/api/teacher/hours', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    // The read-back state is the server's, never something she sends.
    // A hold only on a Friday-to-Sunday stretch with hours, and the hold fields
    // only when there is one, so an ordinary week is sent as it always was.
    body: JSON.stringify({
      days: days.map(({ day, startTime, endTime, holdRoomId }) =>
        holdRoomId && startTime !== null && mayHold(day)
          ? { day, startTime, endTime, holdRoomId }
          : { day, startTime, endTime },
      ),
      ...(holdChoices.length > 0 ? { holdChoices } : {}),
    }),
  })

  if (res.status === 409) {
    const body = await res.json().catch(() => null)
    if (Array.isArray(body?.clashes)) throw new HoldClashError(body.clashes)
    throw new Error(body?.detail ?? body?.title ?? 'Could not save (409)')
  }

  if (!res.ok) {
    // The server explains the refusal - an overlap, a half-filled row, a day
    // that is both hours and not-in. Passing it through beats a status code.
    const problem = await res.json().catch(() => null)
    throw new Error(problem?.detail ?? problem?.title ?? `Could not save (${res.status})`)
  }

  return res.json()
}

/**
 * Whether a weekday has hours, is marked not-in, or has not been set.
 *
 * <b>The three states the whole feature turns on</b>, derived from the rows
 * rather than stored as a fourth field: a day with rows carrying times is
 * <c>hours</c>, a day with a row carrying none is <c>closed</c>, and a day with
 * no row at all is <c>unset</c>.
 */
export function shapeOf(days: HoursRow[], day: Weekday): 'hours' | 'closed' | 'unset' {
  const rows = days.filter((d) => d.day === day)

  if (rows.length === 0) return 'unset'
  return rows.some((r) => r.startTime !== null) ? 'hours' : 'closed'
}

/**
 * The stretches on one weekday, <b>in the order they are held</b>.
 *
 * <b>Deliberately not sorted.</b> Sorting here is what made editing one stretch
 * silently retime the other: the list re-sorts on every render, so typing a
 * start time that moves a row past its neighbour swaps the two under the caret
 * and the next keystroke lands on the wrong one. The server already returns
 * them in time order, so a loaded week reads correctly; what must not happen is
 * a reorder <i>while she is typing</i>.
 */
export function stretchesOn(days: HoursRow[], day: Weekday): HoursRow[] {
  return days.filter((d) => d.day === day && d.startTime !== null)
}

/**
 * Times as an <c>&lt;input type="time"&gt;</c> wants them.
 *
 * <b>The API sends <c>HH:mm:ss</c> and the input wants <c>HH:mm</c></b>, so a
 * value fed straight through renders blank — a returning teacher opens the panel
 * and her published week looks empty. Twelve <c>.slice(0, 5)</c> calls elsewhere
 * in this app do the same trim anonymously; this is the first one with a name,
 * and the others are worth folding into it when something next touches them.
 */
export function clockValue(value: string | null): string {
  return value?.slice(0, 5) ?? ''
}

/**
 * What an emptied time box means.
 *
 * <b>An empty string is not a time and must not travel as one.</b>
 * <c>TimeOnly?</c> refuses <c>""</c> at the binder, before the handler runs — so
 * the sentence the server wrote for a half-filled row ("give each stretch both a
 * start and an end") never reaches her, and she gets a bare 400 instead.
 * Clearing a box to retype it is the ordinary way to change a time.
 */
export function asTime(value: string): string | null {
  return value.trim() === '' ? null : value
}
